// Help context for state management
import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import { HelpContent, FAQItem, TroubleshootingGuide, HelpSearchResult } from '../types/help';
import { helpService } from '../services/helpService';

interface HelpContextType {
  // Help content state
  helpContent: HelpContent[];
  faqs: FAQItem[];
  troubleshootingGuides: TroubleshootingGuide[];
  searchResults: HelpSearchResult[];
  categories: string[];
  
  // Active help state
  activeTooltip: HelpContent | null;
  activeOverlay: HelpContent | null;
  isHelpVisible: boolean;
  
  // Help actions
  loadHelpContent: (component: string, tenantId: string) => Promise<void>;
  loadFAQs: (tenantId: string, category?: string) => Promise<void>;
  loadTroubleshootingGuides: (tenantId: string, category?: string) => Promise<void>;
  searchHelpContent: (query: string, filters: any, tenantId: string) => Promise<void>;
  loadHelpCategories: (tenantId: string) => Promise<void>;
  
  // Tooltip and overlay management
  showTooltip: (component: string, triggerEvent: string, tenantId: string) => Promise<void>;
  hideTooltip: () => void;
  showOverlay: (component: string, tenantId: string) => Promise<void>;
  hideOverlay: () => void;
  toggleHelp: () => void;
  
  // Help content utilities
  getHelpForComponent: (component: string) => HelpContent[];
  getFAQByCategory: (category: string) => FAQItem[];
  getTroubleshootingByCategory: (category: string) => TroubleshootingGuide[];
  
  // Loading states
  loading: boolean;
  error: string | null;
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

interface HelpProviderProps {
  children: ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [helpContent, setHelpContent] = useState<HelpContent[]>([]);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [troubleshootingGuides, setTroubleshootingGuides] = useState<TroubleshootingGuide[]>([]);
  const [searchResults, setSearchResults] = useState<HelpSearchResult[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTooltip, setActiveTooltip] = useState<HelpContent | null>(null);
  const [activeOverlay, setActiveOverlay] = useState<HelpContent | null>(null);
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load help content for a component
  const loadHelpContent = useCallback(async (component: string, tenantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const content = await helpService.getHelpContent(component, tenantId);
      setHelpContent(prev => {
        const filtered = prev.filter(c => c.target_component !== component);
        return [...filtered, ...content];
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load help content');
      console.error('Error loading help content:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load FAQs
  const loadFAQs = useCallback(async (tenantId: string, category?: string) => {
    try {
      setLoading(true);
      setError(null);
      const faqData = await helpService.getFAQ(tenantId, category);
      setFaqs(faqData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load FAQs');
      console.error('Error loading FAQs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load troubleshooting guides
  const loadTroubleshootingGuides = useCallback(async (tenantId: string, category?: string) => {
    try {
      setLoading(true);
      setError(null);
      const guides = await helpService.getTroubleshootingGuides(tenantId, category);
      setTroubleshootingGuides(guides);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load troubleshooting guides');
      console.error('Error loading troubleshooting guides:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Search help content
  const searchHelpContent = useCallback(async (query: string, filters: any, tenantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const results = await helpService.searchHelpContent({
        query,
        ...filters
      }, tenantId);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search help content');
      console.error('Error searching help content:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load help categories
  const loadHelpCategories = useCallback(async (tenantId: string) => {
    try {
      setLoading(true);
      setError(null);
      const categoryData = await helpService.getHelpCategories(tenantId);
      setCategories(categoryData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load help categories');
      console.error('Error loading help categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Show tooltip for component
  const showTooltip = useCallback(async (component: string, triggerEvent: string, tenantId: string) => {
    try {
      const content = await helpService.getTooltipContent(component, triggerEvent, tenantId);
      if (content) {
        setActiveTooltip(content);
        // Track tooltip usage
        await helpService.trackHelpUsage(content.id, 'view', tenantId);
      }
    } catch (err) {
      console.error('Error showing tooltip:', err);
    }
  }, []);

  // Hide tooltip
  const hideTooltip = useCallback(() => {
    setActiveTooltip(null);
  }, []);

  // Show overlay for component
  const showOverlay = useCallback(async (component: string, tenantId: string) => {
    try {
      const content = await helpService.getOverlayContent(component, tenantId);
      if (content) {
        setActiveOverlay(content);
        // Track overlay usage
        await helpService.trackHelpUsage(content.id, 'view', tenantId);
      }
    } catch (err) {
      console.error('Error showing overlay:', err);
    }
  }, []);

  // Hide overlay
  const hideOverlay = useCallback(() => {
    setActiveOverlay(null);
  }, []);

  // Toggle help visibility
  const toggleHelp = useCallback(() => {
    setIsHelpVisible(prev => !prev);
  }, []);

  // Get help content for specific component
  const getHelpForComponent = useCallback((component: string): HelpContent[] => {
    return helpContent.filter(c => c.target_component === component && c.is_active);
  }, [helpContent]);

  // Get FAQs by category
  const getFAQByCategory = useCallback((category: string): FAQItem[] => {
    return faqs.filter(faq => faq.category === category && faq.is_active);
  }, [faqs]);

  // Get troubleshooting guides by category
  const getTroubleshootingByCategory = useCallback((category: string): TroubleshootingGuide[] => {
    return troubleshootingGuides.filter(guide => guide.category === category && guide.is_active);
  }, [troubleshootingGuides]);

  const value = useMemo(() => ({
    helpContent,
    faqs,
    troubleshootingGuides,
    searchResults,
    categories,
    activeTooltip,
    activeOverlay,
    isHelpVisible,
    loadHelpContent,
    loadFAQs,
    loadTroubleshootingGuides,
    searchHelpContent,
    loadHelpCategories,
    showTooltip,
    hideTooltip,
    showOverlay,
    hideOverlay,
    toggleHelp,
    getHelpForComponent,
    getFAQByCategory,
    getTroubleshootingByCategory,
    loading,
    error
  }), [
    helpContent,
    faqs,
    troubleshootingGuides,
    searchResults,
    categories,
    activeTooltip,
    activeOverlay,
    isHelpVisible,
    loadHelpContent,
    loadFAQs,
    loadTroubleshootingGuides,
    searchHelpContent,
    loadHelpCategories,
    showTooltip,
    hideTooltip,
    showOverlay,
    hideOverlay,
    toggleHelp,
    getHelpForComponent,
    getFAQByCategory,
    getTroubleshootingByCategory,
    loading,
    error
  ]);

  return (
    <HelpContext.Provider value={value}>
      {children}
    </HelpContext.Provider>
  );
};

export const useHelp = (): HelpContextType => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};
